import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography, Tooltip, Grid, Chip, Divider, IconButton, useMediaQuery, useTheme, Collapse } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import VerifiedIcon from '@mui/icons-material/Verified';
import UpdateIcon from '@mui/icons-material/Update';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { CTM_TIERS, normalizeCtmTier } from '../StandardDialog/utils/ProjectDialogUtils';

const StandardHighlight = ({
  lastUpdated,
  certificationDate,
  renewalDate,
  requirementTypeStats,
  rows = [],
  onClose,
  customContent = null,
  onExpandChange = null, // Callback when expand/collapse state changes
  frameworkAcronym,
  tierStats,
  projectCoverage,
}) => {
  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'Not set';
    
    const dateObj = new Date(date);
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    const month = months[dateObj.getMonth()];
    const day = dateObj.getDate();
    const year = dateObj.getFullYear();
    
    return `${month} ${day}, ${year}`;
  };

  // Calculate more complete stats from rows if needed
  const calculatedStats = useMemo(() => {
    if (!rows || rows.length === 0) return null;
    
    const stats = {
      shall: { total: 0, implemented: 0, no: 0, needAction: 0, notApplicable: 0 },
      should: { total: 0, implemented: 0, no: 0, needAction: 0, notApplicable: 0 }
    };
    
    rows.forEach(control => {
      const status = control.framework_control_organization?.[0]?.fco_status || "";
      const isMandatory = control.is_mandatory === true;
      const statType = isMandatory ? 'shall' : 'should';
      
      stats[statType].total++;
      
      if (status === "Yes" || status === "Not Applicable") {
        stats[statType].implemented++;
      } else if (status === "No") {
        stats[statType].no++;
      } else if (status === "Need Action") {
        stats[statType].needAction++;
      } else if (status === "Not Applicable") {
        stats[statType].notApplicable++;
      }
    });
    
    return stats;
  }, [rows]);

  // Combine passed stats with calculated stats
  const finalStats = useMemo(() => {
    // If no requirementTypeStats or calculated stats, return empty default
    if (!requirementTypeStats && !calculatedStats) {
      return {
        shall: { total: 0, implemented: 0 },
        should: { total: 0, implemented: 0 }
      };
    }
    
    // If no calculatedStats (no rows provided), just use the requirementTypeStats
    if (!calculatedStats) return requirementTypeStats;
    
    // If no requirementTypeStats, just use the calculatedStats
    if (!requirementTypeStats) return calculatedStats;
    
    // Otherwise combine them, preferring the requirementTypeStats values
    return {
      shall: {
        total: requirementTypeStats.shall?.total || calculatedStats.shall.total,
        implemented: requirementTypeStats.shall?.implemented || calculatedStats.shall.implemented,
        no: requirementTypeStats.shall?.no || calculatedStats.shall.no,
        needAction: requirementTypeStats.shall?.needAction || calculatedStats.shall.needAction,
        notApplicable: requirementTypeStats.shall?.notApplicable || calculatedStats.shall.notApplicable
      },
      should: {
        total: requirementTypeStats.should?.total || calculatedStats.should.total,
        implemented: requirementTypeStats.should?.implemented || calculatedStats.should.implemented,
        no: requirementTypeStats.should?.no || calculatedStats.should.no,
        needAction: requirementTypeStats.should?.needAction || calculatedStats.should.needAction,
        notApplicable: requirementTypeStats.should?.notApplicable || calculatedStats.should.notApplicable
      }
    };
  }, [requirementTypeStats, calculatedStats]);

  // Calculate percentage for display
  const calculatePercentage = (implemented, total) => {
    if (!total) return 0;
    return Math.round((implemented / total) * 100);
  };

  // Get color based on percentage
  const getColorByPercentage = (percentage) => {
    if (percentage >= 100) return '#4CAF50'; // Deep green for 100%
    if (percentage >= 80) return '#4CAF50'; // Green for 80% or above
    return '#F6A200'; // Amber for below 80%
  };

  // Calculate percentages
  const shallPercentage = calculatePercentage(
    finalStats.shall?.implemented || 0,
    finalStats.shall?.total || 0
  );
  
  const shouldPercentage = calculatePercentage(
    finalStats.should?.implemented || 0, 
    finalStats.should?.total || 0
  );

  // CTM:2025 — tier-specific derived values
  const isCTM2025 = frameworkAcronym === 'CTM:2025';

  const targetTier = useMemo(() => {
    if (!isCTM2025 || !Array.isArray(projectCoverage)) return null;
    return projectCoverage.map(normalizeCtmTier).find(Boolean) || null;
  }, [isCTM2025, projectCoverage]);

  const targetTierKey = targetTier?.key || null;

  // Build per-tier and cumulative (mandatory) stats
  const tierBreakdown = useMemo(() => {
    if (!isCTM2025 || !tierStats) return [];
    let cumImpl = 0;
    let cumTotal = 0;
    return CTM_TIERS.map(({ key, value, tierNumber }) => {
      const s = tierStats[key] || { total: 0, implemented: 0 };
      cumImpl += s.implemented;
      cumTotal += s.total;
      return { key, label: value, num: tierNumber, impl: s.implemented, total: s.total, cumImpl, cumTotal };
    });
  }, [isCTM2025, tierStats]);

  // Responsive collapse functionality
  const theme = useTheme();
  const isVeryLargeScreen = useMediaQuery('(min-width:2560px)'); // Very large screens > 2560px - only expanded here
  const [contentCollapsed, setContentCollapsed] = useState(!isVeryLargeScreen);

  // Notify parent of initial state on mount
  useEffect(() => {
    if (onExpandChange) {
      onExpandChange(!contentCollapsed); // Pass expanded state (inverse of collapsed)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Update collapse state when screen size changes
  useEffect(() => {
    const newCollapsed = !isVeryLargeScreen;
    setContentCollapsed(newCollapsed);
    if (onExpandChange) {
      onExpandChange(!newCollapsed); // Pass expanded state
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVeryLargeScreen]);

  const handleToggleContent = () => {
    const newState = !contentCollapsed;
    setContentCollapsed(newState);
    // Notify parent if callback provided
    if (onExpandChange) {
      onExpandChange(!newState); // Pass expanded state (inverse of collapsed)
    }
  };

  return (
    <Box 
      sx={{ 
        position: 'relative',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        mb: 1,
        overflow: 'hidden',
        border: '1px solid #eaeaea',
        width: '100%',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          background: 'linear-gradient(to bottom, #4361ee, #3f37c9)'
        }
      }}
    >
      <Grid container spacing={0}>
        {/* Title and Highlights */}
        <Grid item xs={12}>
          <Box 
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 0.75,
              borderBottom: '1px solid #f0f0f0',
              background: 'linear-gradient(90deg, rgba(242,246,255,0.5) 0%, rgba(255,255,255,0) 100%)',
              justifyContent: 'space-between',
              cursor: 'pointer', // Always clickable
              '&:hover': {
                background: 'linear-gradient(90deg, rgba(242,246,255,0.7) 0%, rgba(255,255,255,0.1) 100%)',
              }
            }}
            onClick={handleToggleContent} // Always clickable
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: 24,
                height: 24,
                borderRadius: '6px',
                background: 'linear-gradient(135deg, #4361ee 0%, #3f37c9 100%)',
                boxShadow: '0 2px 4px rgba(67, 97, 238, 0.2)',
                mr: 1.5
              }}>
                <LightbulbIcon sx={{ color: '#fff', fontSize: 16 }} />
              </Box>
              <Typography sx={{ 
                fontSize: '14px', 
                fontWeight: 600, 
                color: '#333'
              }}>
                Key Highlights
              </Typography>
              <IconButton 
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleContent();
                }}
                sx={{ 
                  ml: 1,
                  color: '#666',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    color: '#333'
                  },
                  padding: '2px'
                }}
                aria-label={contentCollapsed ? "expand content" : "collapse content"}
              >
                {contentCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
              </IconButton>
            </Box>
            
            {onClose && (
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                sx={{ 
                  color: '#666',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    color: '#333'
                  },
                  padding: '4px'
                }}
                aria-label="close"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Grid>

        {/* Content section */}
        <Collapse in={!contentCollapsed} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
          {customContent ? (
            <Box sx={{ py: 1, px: 1.5, width: '100%' }}>
              {customContent}
            </Box>
          ) : (
          <Grid container sx={{ py: 0.75, px: 1.5, alignItems: 'stretch', width: '100%' }}>
          {/* Dates Section */}
          <Grid item xs={12} md={isCTM2025 ? 3.25 : 5} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pr: { xs: 0, md: isCTM2025 ? 1.5 : 0 } }}>
            {/* Last Updated */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: 22,
                height: 22,
                borderRadius: '50%',
                backgroundColor: 'rgba(98, 106, 229, 0.08)',
                mr: 1.5,
                flexShrink: 0
              }}>
                <UpdateIcon sx={{ color: '#4361ee', fontSize: 14 }} />
              </Box>
              <Typography sx={{ fontSize: '13px', color: '#666', fontWeight: 500, mr: 1 }}>
                Last Updated:
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#333', fontWeight: 600 }}>
                {formatDate(lastUpdated)}
              </Typography>
            </Box>

            {/* Certification Date */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: 22,
                height: 22,
                borderRadius: '50%',
                backgroundColor: 'rgba(98, 106, 229, 0.08)',
                mr: 1.5,
                flexShrink: 0
              }}>
                <VerifiedIcon sx={{ color: '#4361ee', fontSize: 14 }} />
              </Box>
              <Typography sx={{ fontSize: '13px', color: '#666', fontWeight: 500, mr: 1 }}>
                Certification:
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#333', fontWeight: 600 }}>
                {formatDate(certificationDate)}
              </Typography>
            </Box>

            {/* Renewal Date */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: 22,
                height: 22,
                borderRadius: '50%',
                backgroundColor: 'rgba(98, 106, 229, 0.08)',
                mr: 1.5,
                flexShrink: 0
              }}>
                <EventIcon sx={{ color: '#4361ee', fontSize: 14 }} />
              </Box>
              <Typography sx={{ fontSize: '13px', color: '#666', fontWeight: 500, mr: 1 }}>
                Renewal:
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#333', fontWeight: 600 }}>
                {formatDate(renewalDate)}
              </Typography>
            </Box>

            {/* Target Tier — CTM:2025 only */}
            {isCTM2025 && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(98, 106, 229, 0.08)',
                  mr: 1.5,
                  flexShrink: 0
                }}>
                  <EmojiEventsIcon sx={{ color: '#4361ee', fontSize: 14 }} />
                </Box>
                <Typography sx={{ fontSize: '13px', color: '#666', fontWeight: 500, mr: 1 }}>
                  Target Tier:
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#333', fontWeight: 600 }}>
                  {targetTier?.shortLabel || 'Not set'}
                </Typography>
              </Box>
            )}
          </Grid>

          {/* Vertical Divider */}
          <Grid item xs={0} md={isCTM2025 ? 0.25 : 0.5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
            <Divider orientation="vertical" sx={{ height: '100%' }} />
          </Grid>

          {/* Requirements Status */}
          <Grid item xs={12} md={isCTM2025 ? 8.5 : 6.5} sx={{ 
            mt: { xs: 1, md: 0 },
            borderTop: { xs: '1px solid #f0f0f0', md: 'none' },
            pt: { xs: 1, md: 0 },
            display: 'flex',
            justifyContent: 'flex-end',
            pl: { xs: 0, md: isCTM2025 ? 1.5 : 2 }
          }}>
            {isCTM2025 ? (
              /* ── CTM:2025: 5-tier card grid ── */
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(auto-fit, minmax(92px, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
                gap: 0.75,
                width: '100%',
              }}>
                {tierBreakdown.map(({ key, label, num, impl, total, cumImpl, cumTotal }) => {
                  const isTarget = key === targetTierKey;
                  const tierPct = total > 0 ? Math.round((impl / total) * 100) : 0;
                  const mandatoryPct = cumTotal > 0 ? Math.round((cumImpl / cumTotal) * 100) : 0;
                  const tierColor = tierPct >= 100 ? '#4CAF50' : '#F6A200';
                  const mandatoryColor = mandatoryPct >= 100 ? '#4CAF50' : '#F6A200';
                  const tierBg = tierPct >= 100 ? '#F2F9F3' : '#FFFAF0';
                  const mandatoryBg = mandatoryPct >= 100 ? '#F2F9F3' : '#FFFAF0';
                  const tierBorder = tierPct >= 100 ? '#D5ECD8' : '#FFEBC2';
                  const mandatoryBorder = mandatoryPct >= 100 ? '#D5ECD8' : '#FFEBC2';
                  return (
                    <Box
                      key={key}
                      sx={{
                        minWidth: 0,
                        border: isTarget ? '1.5px solid #4361ee' : '1px solid #eaeaea',
                        borderRadius: '8px',
                        p: 0.75,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        backgroundColor: isTarget ? 'rgba(67,97,238,0.03)' : '#fff',
                      }}
                    >
                      {/* Tier header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '10px', color: '#888', fontWeight: 500 }}>T{num}</Typography>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: isTarget ? '#4361ee' : '#333' }}>
                          {label}
                        </Typography>
                        {isTarget && (
                          <Chip
                            label="Target"
                            size="small"
                            sx={{ fontSize: '9px', height: '16px', backgroundColor: '#EEF1FD', color: '#4361ee', fontWeight: 600, '& .MuiChip-label': { px: 0.75 } }}
                          />
                        )}
                      </Box>

                      <Divider sx={{ borderColor: '#f0f0f0', my: -0.25 }} />

                      {/* This Tier */}
                      <Box>
                        <Typography sx={{ fontSize: '9px', color: '#999', fontWeight: 500, mb: 0.25 }}>This Tier</Typography>
                        <Box sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          backgroundColor: tierBg, border: `1px solid ${tierBorder}`,
                          borderRadius: '12px', px: 0.75, height: '18px',
                        }}>
                          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: tierColor }}>
                            {tierPct}% ({impl}/{total})
                          </Typography>
                        </Box>
                      </Box>

                      {/* Mandatory */}
                      <Box>
                        <Typography sx={{ fontSize: '9px', color: '#999', fontWeight: 500, mb: 0.25 }}>Mandatory</Typography>
                        <Box sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          backgroundColor: mandatoryBg, border: `1px solid ${mandatoryBorder}`,
                          borderRadius: '12px', px: 0.75, height: '18px',
                        }}>
                          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: mandatoryColor }}>
                            {mandatoryPct}% ({cumImpl}/{cumTotal})
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              /* ── Default: SHALL / SHOULD ── */
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%' }}>
                {/* Left side: SHALL and SHOULD requirements */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                  {/* SHALL requirements */}
                  <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '32px', pt: 0.5 }}>
                    <Chip
                      label="SHALL"
                      size="small"
                      sx={{ fontSize: '11px', fontWeight: 600, height: '22px', backgroundColor: '#FFE9EA', color: '#E63946', mr: 1 }}
                    />
                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#666', mr: 1 }}>
                      (Mandatory Controls)
                    </Typography>
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography sx={{ fontWeight: 'bold', mb: 1 }}>SHALL Requirements</Typography>
                          <Typography variant="body2">
                            Mandatory requirements that must be implemented for compliance.
                            These are critical controls that are essential for certification.
                          </Typography>
                          <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                            <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'flex', alignItems: 'center' }}>
                              <TipsAndUpdatesIcon sx={{ color: 'rgba(255, 255, 255, 0.9)', mr: 1, fontSize: 16 }} />
                              For certification to be awarded, 100% of SHALL requirements must be met.
                            </Typography>
                          </Box>
                        </Box>
                      }
                      arrow
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: '#999', cursor: 'help' }} />
                    </Tooltip>
                  </Box>

                  {/* SHOULD requirements */}
                  <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '32px', pt: 0.5 }}>
                    <Chip
                      label="SHOULD"
                      size="small"
                      sx={{ fontSize: '11px', fontWeight: 600, height: '22px', backgroundColor: '#E8F5E9', color: '#2E7D32', mr: 1 }}
                    />
                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#666', mr: 1 }}>
                      (Good to Have Controls)
                    </Typography>
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography sx={{ fontWeight: 'bold', mb: 1 }}>SHOULD Requirements</Typography>
                          <Typography variant="body2">
                            Recommended requirements that enhance security posture.
                            While not strictly mandatory, these controls demonstrate a mature approach to security.
                          </Typography>
                          <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                            <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'flex', alignItems: 'center' }}>
                              <TipsAndUpdatesIcon sx={{ color: 'rgba(255, 255, 255, 0.9)', mr: 1, fontSize: 16 }} />
                              If you plan to pursue higher level certifications such as Cyber TrustMark (CTM), ISO27001 or Data Protection Trust Mark (DPTM), these controls become mandatory and should be implemented before starting those certification processes.
                            </Typography>
                          </Box>
                        </Box>
                      }
                      arrow
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: '#999', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                </Box>

                {/* Right side: Percentage chips */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, justifyContent: 'flex-start', pt: 0.5, minWidth: '140px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '32px' }}>
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: shallPercentage < 100 ? '#FFFAF0' : '#F2F9F3',
                      border: `1px solid ${shallPercentage < 100 ? '#FFEBC2' : '#D5ECD8'}`,
                      borderRadius: '18px', px: 1.5, py: 0.5, height: '22px', width: '140px'
                    }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: shallPercentage >= 100 ? '#4CAF50' : '#F6A200' }}>
                        {shallPercentage}% ({finalStats.shall?.implemented || 0}/{finalStats.shall?.total || 0})
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '32px' }}>
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: shouldPercentage < 80 ? '#FFFAF0' : '#F2F9F3',
                      border: `1px solid ${shouldPercentage < 80 ? '#FFEBC2' : '#D5ECD8'}`,
                      borderRadius: '18px', px: 1.5, py: 0.5, height: '22px', width: '140px'
                    }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: shouldPercentage >= 80 ? '#4CAF50' : '#F6A200' }}>
                        {shouldPercentage}% ({finalStats.should?.implemented || 0}/{finalStats.should?.total || 0})
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Grid>
          </Grid>
          )}
        </Collapse>
      </Grid>
    </Box>
  );
};

export default StandardHighlight; 